<template>
  <div class="relative">
    <div @paste="onPaste" ref="anyPlace" class="p-6 space-y-4">
      <!-- Page header -->
      <div class="flex items-center gap-3">
        <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
          <span class="material-icons-round">edit_road</span>
        </span>
        <div>
          <h2 class="text-lg font-semibold text-gray-800">Manual Trip Booking</h2>
          <p class="text-sm text-gray-500">Book a trip on behalf of a passenger.</p>
        </div>
      </div>

      <div class="flex items-center space-x-2">
        <span class="text-gray-500 material-icons-round">add_location_alt</span>
        <h2 class="text-lg font-semibold text-gray-800">Add Scheduled Trip</h2>
      </div>
      <form @submit.prevent class="space-y-4">
        <div
          v-if="type !== 'corporate'"
          class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
        >
          <div class="flex items-center mb-4 space-x-1 text-gray-500">
            <span class="text-base material-icons-round">person</span>
            <span class="text-xs font-semibold tracking-widest uppercase">Passenger</span>
          </div>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div class="relative">
              <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Contact Number</label>
              <div class="flex items-center mt-1.5">
                <span class="pr-2 text-sm font-semibold text-gray-500">+251</span>
                <input
                  required
                  minlength="9"
                  maxlength="9"
                  v-model="phoneNumber"
                  class="w-full h-10 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                  placeholder="912 345678"
                  @keyup="searchRiders(phoneNumber)"
                />
              </div>
              <div v-if="passengers.length" class="absolute inset-x-0 z-50 mt-2">
                <ul class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64">
                  <li
                    v-for="(result, index) in passengers"
                    :key="`pickadd-${index}`"
                    class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                    :class="{ 'bg-red-100 cursor-not-allowed': result.disabled }"
                    @click="selectRider(result)"
                  >
                    <p class="text-sm font-semibold text-gray-800">{{ result.firstName + " " + result.lastName }}</p>
                    <p class="text-xs text-gray-500">{{ result.phoneNumber }}</p>
                  </li>
                </ul>
              </div>
            </div>
            <div>
              <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Name</label>
              <input
                v-model="riderName"
                class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="Melaku Wedajo"
              />
            </div>
            <div>
              <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Email</label>
              <input
                v-model="email"
                class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                placeholder="melaku.wedajo@domain.com"
              />
            </div>
          </div>
        </div>

        <div class="flex flex-col items-start gap-4 xl:flex-row">
          <div class="w-full xl:w-96 xl:flex-shrink-0">
            <div class="p-4 space-y-4 bg-white border border-gray-200 rounded-2xl shadow-sm">
              <div class="flex items-center space-x-1 text-gray-500">
                <span class="text-base material-icons-round">route</span>
                <span class="text-xs font-semibold tracking-widest uppercase">Trip Details</span>
              </div>
              <div v-if="type !== 'delivery'">
                <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Vehicle Type</label>
                <select
                  @change="setVehicleType"
                  class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                >
                  <option value disabled selected hidden>Select a vehicle type...</option>
                  <option
                    v-for="type in vehicleTypes"
                    :key="type._id"
                    :value="type._id"
                    :selected="vehicleType === type._id"
                  >{{ type.name }}</option>
                </select>
              </div>

              <div class="flex gap-3">
                <div class="w-1/2">
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Type</label>
                  <select
                    v-model="type"
                    class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                  >
                    <option value="normal">Normal</option>
                    <option value="bid">Bid</option>
                    <option value="rent">Rent</option>
                    <option value="corporate">Corporate</option>
                    <option value="delivery">Delivery</option>
                  </select>
                </div>

                <div v-if="type == 'bid'" class="w-1/2">
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Amount</label>
                  <input
                    class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                    type="number"
                    v-model="bidAmount"
                    required
                    placeholder="Amount"
                    :min="baseFare"
                  />
                </div>

                <div class="w-1/2" v-if="type == 'normal'">
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Schedule</label>
                  <select
                    v-model="scheduled"
                    class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                  >
                    <option :value="false">Now</option>
                    <option :value="true">Scheduled</option>
                  </select>
                </div>
              </div>
              <div v-if="type == 'corporate'" class="flex w-full gap-3">
                <div class="w-1/2">
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Corporate</label>
                  <p v-if="loadingCorps" class="mt-1.5 text-sm text-gray-500">Loading corporates...</p>
                  <select
                    v-else
                    class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                    v-model="selectedCorporate"
                  >
                    <option
                      v-for="corp in corporatesList"
                      :key="corp._id"
                      :value="corp._id"
                    >{{ corp.name }}</option>
                  </select>
                </div>
                <div class="w-1/2" v-if="selectedCorporate !== null">
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Employee</label>
                  <p v-if="loadingEmployees" class="mt-1.5 text-sm text-gray-500">Loading employees...</p>
                  <select
                    v-else
                    class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                    v-model="selectedEmployee"
                  >
                    <option
                      v-for="employee in employeesList"
                      :key="employee._id"
                      :value="employee._id"
                    >{{ employee.name }}</option>
                  </select>
                </div>
              </div>

              <div v-if="scheduled && type == 'normal'" class="flex gap-3">
                <div class="w-1/2">
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Pickup Date</label>
                  <v-date-picker v-model="date" class="w-full">
                    <template v-slot="{ inputValue, inputEvents }">
                      <input
                        class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 bg-white border border-gray-300 shadow-sm rounded-xl focus:border-taxiye-300"
                        :value="inputValue"
                        v-on="inputEvents"
                      />
                    </template>
                  </v-date-picker>
                </div>
                <div class="flex w-1/2 gap-3">
                  <div class="w-1/2">
                    <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Hour</label>
                    <select
                      v-model="hour"
                      class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                    >
                      <option v-for="i in 24" :key="i" :value="i">{{ i }}</option>
                    </select>
                  </div>
                  <div class="w-1/2">
                    <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Minute</label>
                    <select
                      v-model="minute"
                      class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                    >
                      <option
                        v-for="i in 12"
                        :key="i"
                        :value="(i - 1) * 5"
                      >{{ ((i - 1) * 5).toString().padStart(2, "0") }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div v-if="type == 'rent'" class="flex gap-3">
                <div class="w-1/3">
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Hour</label>
                  <select
                    v-model="rentHour"
                    class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                  >
                    <option v-for="i in 24" :key="i" :value="i">{{ i }}</option>
                  </select>
                </div>
                <div class="w-1/3">
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Day</label>
                  <select
                    v-model="rentDay"
                    class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                  >
                    <option :value="0">{{ 0 }}</option>
                    <option v-for="i in 31" :key="i" :value="i">{{ i }}</option>
                  </select>
                </div>
                <div class="w-1/3">
                  <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Month</label>
                  <select
                    v-model="rentMonth"
                    class="w-full h-10 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-select rounded-xl focus:border-taxiye-300"
                  >
                    <option :value="0">{{ 0 }}</option>
                    <option v-for="i in 12" :key="i" :value="i">{{ i }}</option>
                  </select>
                </div>
              </div>

              <div class="space-y-4">
                <OriginAddressSearchBox
                  required
                  :initialValue="pickUpAddress"
                  v-model="pickUpAddress"
                  :customPlace="customPickupAddress"
                  v-on:input="estimate"
                  :map="map"
                />
                <DestinationAddressSearchBox
                  v-if="type != 'rent'"
                  required
                  :initialValue="destinationAddress"
                  v-model="destinationAddress"
                  v-on:input="estimate"
                />
              </div>

              <div class="relative">
                <label class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Assign Driver</label>
                <div class="relative mt-1.5">
                  <span class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <span class="text-base text-gray-500 material-icons-round">search</span>
                  </span>
                  <input
                    class="w-full h-10 pl-9 pr-3 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
                    type="text"
                    v-model="driverName"
                    placeholder="Search for drivers..."
                    v-on:keyup="searchDriver(driverName)"
                  />
                </div>
                <div v-if="drivers.length" class="absolute inset-x-0 z-50 mt-2">
                  <ul class="py-1 overflow-y-auto bg-white border border-gray-100 shadow-lg rounded-xl max-h-64">
                    <li
                      v-for="(result, index) in drivers"
                      :key="`pickadd-${index}`"
                      class="px-3 py-2 cursor-pointer transition-colors duration-150 hover:bg-taxiye-50"
                      :class="{ 'bg-red-100 cursor-not-allowed': result.disabled }"
                      @click="selectDriver(result)"
                    >
                      <p class="text-sm font-semibold text-gray-800">{{ result.firstName + " " + result.lastName }}</p>
                      <p class="text-xs text-gray-500">{{ result.phoneNumber }}</p>
                    </li>
                  </ul>
                </div>
              </div>
              <div>
                <button
                  v-if="type === 'corporate'"
                  @click="dispatchCorporate()"
                  :disabled="!validCorporate"
                  type="submit"
                  class="flex items-center justify-center w-full text-sm font-semibold text-white transition-colors duration-150 shadow-sm h-11 rounded-xl"
                  :class="
                    !validCorporate
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-taxiye-500 hover:bg-taxiye-600'
                  "
                >
                  <span v-if="dispatching" class="flex items-center space-x-2">
                    <i class="text-base material-icons spin">sync</i>
                    <span>Dispatching</span>
                  </span>
                  <span v-else>Dispatch</span>
                </button>
                <button
                  v-else
                  @click="add()"
                  :disabled="dispatching"
                  type="submit"
                  class="flex items-center justify-center w-full text-sm font-semibold text-white transition-colors duration-150 shadow-sm h-11 rounded-xl"
                  :class="
                    dispatching
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-taxiye-500 hover:bg-taxiye-600'
                  "
                >
                  <span v-if="dispatching" class="flex items-center space-x-2">
                    <i class="text-base material-icons spin">sync</i>
                    <span>Dispatching</span>
                  </span>
                  <span v-else>Dispatch</span>
                </button>
              </div>
            </div>
          </div>
          <div class="w-full min-w-0 space-y-4 xl:flex-grow">
            <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm" v-if="type === 'rent'">
              <div class="flex items-center mb-3 space-x-1 text-gray-500">
                <span class="text-base material-icons-round">receipt_long</span>
                <span class="text-xs font-semibold tracking-widest uppercase">Rent Estimate</span>
              </div>
              <div class="flex items-center justify-between py-2 text-sm border-t border-b border-gray-100">
                <span class="text-gray-500">Base Fare</span>
                <span class="font-semibold text-gray-800">ETB {{ baseFare }}</span>
              </div>
              <div class="flex items-center justify-between py-2 text-sm border-b border-gray-100">
                <span class="text-gray-500">Estimated Fare Total</span>
                <span class="font-semibold text-gray-800">ETB {{ estimatedFare }}</span>
              </div>
              <div
                class="flex items-center justify-between py-2 text-sm border-b border-gray-100"
                v-if="estimatedArrivalTime"
              >
                <span class="text-gray-500">Estimated Driver Arrival Time</span>
                <span class="font-semibold text-gray-800">{{ estimatedArrivalTime }} mins.</span>
              </div>
            </div>
            <div class="p-4 bg-white border border-gray-200 rounded-2xl shadow-sm" v-else>
              <div class="flex items-center mb-3 space-x-1 text-gray-500">
                <span class="text-base material-icons-round">receipt_long</span>
                <span class="text-xs font-semibold tracking-widest uppercase">Trip Estimate</span>
              </div>
              <div class="flex items-center justify-between py-2 text-sm border-t border-b border-gray-100">
                <span class="text-gray-500">Base Fare</span>
                <span class="font-semibold text-gray-800">ETB {{ baseFare }}</span>
              </div>
              <div class="flex items-center justify-between py-2 text-sm border-b border-gray-100">
                <span class="text-gray-500">Distance</span>
                <span class="font-semibold text-gray-800">{{ (distance / 1000).toFixed(2) }} KM</span>
              </div>
              <div class="flex items-center justify-between py-2 text-sm border-b border-gray-100">
                <span class="text-gray-500">ETA</span>
                <span class="font-semibold text-gray-800">{{ durationString }} Hrs</span>
              </div>
              <div class="flex items-center justify-between py-2 text-sm border-b border-gray-100">
                <span class="text-gray-500">Estimated Fare Total</span>
                <span class="font-semibold text-gray-800">ETB {{ estimatedFare }}</span>
              </div>
              <div
                class="flex items-center justify-between py-2 text-sm border-b border-gray-100"
                v-if="estimatedArrivalTime"
              >
                <span class="text-gray-500">Estimated Driver Arrival Time</span>
                <span class="font-semibold text-gray-800">{{ estimatedArrivalTime }} mins.</span>
              </div>
            </div>
            <div class="h-64" style="min-height: 45vh">
              <div class="w-full h-full overflow-hidden bg-white border border-gray-200 rounded-2xl shadow-sm">
                <MapBox
                  :route1="route1"
                  :markers="markers"
                  :mapCenter="mapCenter"
                  :mapZoom="mapZoom"
                  popupData="popup"
                  @markerClicked="selectDriverFromMap"
                ></MapBox>
              </div>
            </div>
          </div>
          <div class="w-full xl:w-96 xl:flex-shrink-0" v-if="type === 'rent'">
            <div class="flex flex-col gap-4" v-if="rentSearch">
              <div
                class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
                v-if="rentSearch.status === 'SINGLE_DRIVER'"
              >
                <span class="text-sm font-medium text-gray-700">Specific driver rent request</span>
              </div>
              <div
                class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
                v-else-if="rentSearch.status === 'IN_PROGRESS'"
              >
                <span class="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <i class="text-base material-icons spin text-taxiye-500">sync</i>
                  <span>Searching for rents...</span>
                </span>
                <button
                  class="h-10 px-4 text-sm font-semibold text-white transition-colors duration-150 shadow-sm rounded-xl bg-taxiye-500 hover:bg-taxiye-600"
                  @click.prevent="cancelRent()"
                >Cancel</button>
              </div>
              <div
                class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
                v-else-if="rentSearch.status === 'NO_DRIVERS_FOUND'"
              >
                <span class="text-sm font-semibold text-gray-700">No drivers found</span>
                <button
                  class="h-10 px-4 text-sm font-semibold text-white transition-colors duration-150 shadow-sm rounded-xl bg-taxiye-500 hover:bg-taxiye-600"
                  @click.prevent="retryRent()"
                >Retry</button>
              </div>
              <div
                class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
                v-else-if="rentSearch.status === 'CANCELLED'"
              >
                <span class="text-sm font-medium text-gray-700">Search has been cancelled by {{ rentSearch.cancelledBy }}</span>
                <button
                  class="h-10 px-4 text-sm font-semibold text-white transition-colors duration-150 shadow-sm rounded-xl bg-taxiye-500 hover:bg-taxiye-600"
                  @click.prevent="retryRent()"
                >Retry</button>
              </div>
              <div
                class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
                v-else-if="rentSearch.status === 'COMPLETED'"
              >
                <span class="text-sm font-medium text-gray-700">Rent search has completed</span>
              </div>
              <div class="p-4 space-y-3 overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-sm max-h-64">
                <div class="flex items-center space-x-1 text-gray-500">
                  <span class="text-base material-icons-round">list_alt</span>
                  <span class="text-xs font-semibold tracking-widest uppercase">Requests</span>
                </div>
                <div v-for="(request, index) in rentRequests" :key="index">
                  <div class="p-3 space-y-1 text-xs text-gray-600 border border-gray-200 rounded-xl" v-if="request && request._id">
                    <p>
                      <span class="font-semibold text-gray-800">Passenger</span>
                      -
                      {{
                        request.passenger
                          ? request.passenger.firstName +
                          " " +
                          request.passenger.lastName
                          : "-"
                      }},
                      {{
                        request.passenger ? request.passenger.phoneNumber : "-"
                      }}
                    </p>
                    <p>
                      <span class="font-semibold text-gray-800">Driver</span>
                      -
                      {{
                        request.driver
                          ? request.driver.firstName +
                          " " +
                          request.driver.lastName
                          : "-"
                      }},
                      {{
                        request.driver ? request.driver.phoneNumber : "-"
                      }}
                    </p>
                    <p>
                      <span class="font-semibold text-gray-800">From</span>
                      -
                      {{
                        request.pickUpAddress ? request.pickUpAddress.name : "-"
                      }}
                    </p>
                    <p>
                      <span class="font-semibold text-gray-800">Vehicle type</span>
                      -
                      {{ request.vehicleType.name }}
                    </p>
                    <p>
                      <span class="font-semibold text-gray-800">Status</span>
                      - {{ request.status }}
                      {{
                        request.status === "CANCELLED"
                          ? request.cancelledBy
                            ? "(" + request.cancelledBy + ")"
                            : ""
                          : ""
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-col w-full gap-4 xl:w-96 xl:flex-shrink-0" v-else-if="tripSearch">
            <div
              class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
              v-if="tripSearch.status === 'SINGLE_DRIVER'"
            >
              <span class="text-sm font-medium text-gray-700">Specific driver request</span>
            </div>
            <div
              class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
              v-if="tripSearch.status === 'IN_PROGRESS'"
            >
              <span class="flex items-center space-x-2 text-sm font-medium text-gray-700">
                <i class="text-base material-icons spin text-taxiye-500">sync</i>
                <span>Searching for trips...</span>
              </span>
              <button
                class="h-10 px-4 text-sm font-semibold text-white transition-colors duration-150 shadow-sm rounded-xl bg-taxiye-500 hover:bg-taxiye-600"
                @click.prevent="cancel()"
              >Cancel</button>
            </div>
            <div
              class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
              v-else-if="tripSearch.status === 'NO_DRIVERS_FOUND'"
            >
              <span class="text-sm font-semibold text-gray-700">No drivers found</span>
              <button
                class="h-10 px-4 text-sm font-semibold text-white transition-colors duration-150 shadow-sm rounded-xl bg-taxiye-500 hover:bg-taxiye-600"
                @click.prevent="retry()"
              >Retry</button>
            </div>
            <div
              class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
              v-else-if="tripSearch.status === 'CANCELLED'"
            >
              <span class="text-sm font-medium text-gray-700">Search has been cancelled by {{ tripSearch.cancelledBy }}</span>
              <button
                class="h-10 px-4 text-sm font-semibold text-white transition-colors duration-150 shadow-sm rounded-xl bg-taxiye-500 hover:bg-taxiye-600"
                @click.prevent="retry()"
              >Retry</button>
            </div>
            <div
              class="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-2xl shadow-sm"
              v-else-if="tripSearch.status === 'COMPLETED'"
            >
              <span class="text-sm font-medium text-gray-700">Trip search has completed</span>
            </div>
            <div class="p-4 space-y-3 overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-sm max-h-64">
              <div class="flex items-center space-x-1 text-gray-500">
                <span class="text-base material-icons-round">list_alt</span>
                <span class="text-xs font-semibold tracking-widest uppercase">Requests</span>
              </div>
              <div v-for="(request, index) in requests" :key="index">
                <div class="p-3 space-y-1 text-xs text-gray-600 border border-gray-200 rounded-xl" v-if="request && request._id">
                  <p>
                    <span class="font-semibold text-gray-800">Passenger</span>
                    -
                    {{
                      request.passenger
                        ? request.passenger.firstName +
                        " " +
                        request.passenger.lastName
                        : "-"
                    }},
                    {{
                      request.passenger ? request.passenger.phoneNumber : "-"
                    }}
                  </p>
                  <p>
                    <span class="font-semibold text-gray-800">Driver</span>
                    -
                    {{
                      request.driver
                        ? request.driver.firstName +
                        " " +
                        request.driver.lastName
                        : "-"
                    }},
                    {{
                      request.driver ? request.driver.phoneNumber : "-"
                    }}
                  </p>
                  <p>
                    <span class="font-semibold text-gray-800">From</span>
                    -
                    {{
                      request.pickUpAddress ? request.pickUpAddress.name : "-"
                    }}
                  </p>
                  <p>
                    <span class="font-semibold text-gray-800">To</span>
                    -
                    {{
                      request.dropOffAddress ? request.dropOffAddress.name : ""
                    }}
                  </p>
                  <p>
                    <span class="font-semibold text-gray-800">Vehicle type</span>
                    -
                    {{ request.vehicleType.name }}
                  </p>
                  <p>
                    <span class="font-semibold text-gray-800">Trip type</span>
                    - {{ request.type }}
                  </p>
                  <p>
                    <span class="font-semibold text-gray-800">Status</span>
                    - {{ request.status }}
                    {{
                      request.status === "CANCELLED"
                        ? request.cancelledBy
                          ? "(" + request.cancelledBy + ")"
                          : ""
                        : ""
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <div
      v-if="connecting"
      class="absolute top-0 z-50 flex items-center justify-center w-full h-screen bg-gray-700 bg-opacity-50"
    >
      <div class="flex items-center px-6 py-4 space-x-3 bg-white shadow-lg rounded-2xl">
        <i class="material-icons spin text-taxiye-500">sync</i>
        <span class="text-sm font-semibold text-gray-700">Connecting...</span>
      </div>
    </div>
    <div
      v-if="fetchingTripSearch"
      class="absolute top-0 z-50 flex items-center justify-center w-full h-screen bg-gray-700 bg-opacity-50"
    >
      <div class="flex items-center px-6 py-4 space-x-3 bg-white shadow-lg rounded-2xl">
        <i class="material-icons spin text-taxiye-500">sync</i>
        <span class="text-sm font-semibold text-gray-700">Loading data... please wait</span>
      </div>
    </div>
  </div>
</template>

<script>
import { focus } from "vue-focus";
import OriginAddressSearchBox from "@/components/OriginAddressSearchBox";
import DestinationAddressSearchBox from "@/components/DestinationAddressSearchBox";
import { io } from "socket.io-client";
import { config } from "@/config";
import init from "@/gmaps";
import polyline from "google-polyline";

import MapBox from "../../components/MapBox"

export default {
  directives: {
    focus,
  },
  components: {
    OriginAddressSearchBox,
    DestinationAddressSearchBox,
    MapBox,
    // MglMap,
    // MglGeojsonLayer,

  },
  data() {
    return {
      driverName: "",
      requests: [],
      drivers: [],
      selectedDriver: null,
      estimateFare: 0,
      duration: 0,
      ticketError: false,
      distance: 0,
      passengers: [],
      scheduled: false,
      type: "normal",
      riderId: "",
      fare: 0,
      dispatching: false,
      email: "",
      socket: null,
      phoneNumber: "",
      riderName: "",
      pickUpAddress: {
        name: "",
        coordinate: {},
      },
      destinationAddress: {
        name: "",
        coordinate: {},
      },
      vehicleType: "",
      date: new Date(),
      hour: 1,
      minute: 0,
      rentHour: 1,
      rentDay: 0,
      rentMonth: 0,
      route: [],
      ticket: "",
      ticketId: "",
      ticketLoading: false,
      bidAmount: 0,
      focused: {
        0: true,
        1: false,
        2: false,
      },
      mapsService: null,
      google: null,
      vehicleTypes: [],
      map: null,
      fromMarker: null,
      toMarker: null,
      path: null,
      nearbyDrivers: [],
      searching: false,
      no_drivers_found: false,
      tripSearchId: null,
      tripSearch: null,

      searchingRent: false,
      rentSearchId: null,
      rentSearch: null,
      estimatedArrivalTime: "",
      rentRequests: [],

      customPickupAddress: false,
      connecting: true,

      loadingCorps: false,
      corporatesList: [],
      selectedCorporate: null,

      loadingEmployees: false,
      employeesList: [],
      selectedEmployee: null,

      corporateEstimation: {},

      directionService: {},

      polyline: "",

      route1: [],


      markers: [],
      mapZoom: null,
      mapCenter: {
      },
    };
  },
  async mounted() {
    this.google = await init();

    this.directionService = new this.google.maps.DirectionsService();
    // alert(config.dispatcherBaseURL)
    this.socket = io(`${config.baseURL}/dispatcher-socket`, {
      transports: ["websocket"],
      withCredentials: true,
      upgrade: false,
      // query: {
      //   token: this.$store.getters.token,
      // },
      // rememberUpgrade: true,
      // reconnectionDelayMax: 1000,
      auth: {
        token: this.$store.getters.token,
      },
    });

    this.socket.on("connect", () => {
      this.connecting = false;
      this.socket.emit("init", { id: this.id });
    });

    // Register the event listeners
    this.socket.on("request", (request) => {
      console.log(request);
    });

    this.socket.on("err", (error) => {
      // Without this the button stays disabled on "Dispatching" forever after
      // a rejected dispatch.
      this.dispatching = false;
      window.flash({ type: "error", body: error });
    });

    this.socket.on("tripSearch", (tripSearch) => {
      this.tripSearch = tripSearch;
      this.tripSearchId = tripSearch._id;
      // Only a finished search releases the button - COMPLETED is what a
      // driver accepting looks like here. IN_PROGRESS and SINGLE_DRIVER both
      // arrive while the request is still out with the drivers.
      if (this.searchIsResolved(tripSearch.status)) {
        this.dispatching = false;
      }
    });

    this.socket.on("rentSearch", (rentSearch) => {
      this.rentSearch = rentSearch;
      this.rentSearchId = rentSearch._id;
      if (this.searchIsResolved(rentSearch.status)) {
        this.dispatching = false;
      }
    });

    this.socket.on("noAvailableDriver", () => {
      console.log("No drivers are avialable!");
      window.flash({ type: "error", body: "No drivers are avialable!" });
      this.dispatching = false;
    });

    this.socket.on("requestCanceled", () => {
      console.log("The request has been cancelled!");
      this.dispatching = false;
    });

    this.socket.on("rentRequests", (requests) => {
      console.log({ requests });
      this.rentRequests = requests;
    });

    this.socket.on("requests", (requests) => {
      this.requests = requests;
      // Acceptance reaches this page as a request status change, not only as a
      // "trip" event.
      const accepted = (requests || []).some(
        (request) =>
          request &&
          typeof request.status === "string" &&
          request.status.toUpperCase().startsWith("ACCEPT")
      );
      if (accepted) this.dispatching = false;
    });

    this.socket.on("noDriversFound", () => {
      this.searching = false;
      this.dispatching = false;
      this.no_drivers_found = true;
      window.flash({
        type: "error",
        body: "No nearby drivers found!",
      });
    });

    this.socket.on("trip", (data) => {
      console.log("The request has been accepted!");
      window.flash({ type: "success", body: "Trip is accepted!" });

      // The dispatch is over once a driver accepts, so release the button.
      this.dispatching = false;

      console.log(data);
    });

    this.socket.on("nearbyDrivers", (data) => {
      try {
        this.nearbyDrivers = JSON.parse(data);
      } catch (error) {
        this.nearbyDrivers = [];
      }
    });

    this.socket.on("searching", () => {
      window.flash({
        type: "success",
        body: "The trip has been dispatched!",
      });
      window.events.$emit("dispatched");
      this.searching = true;
      // The button keeps spinning here on purpose - the request has only been
      // sent out, no driver has accepted it yet.
      this.phoneNumber = "";
      this.riderName = "";
      this.email = "";
      this.riderId = "";
      // this.tripSearchId = data.tripSearchId
      this.destinationAddress = {
        name: "",
        coordinate: {}
      };
      this.pickUpAddress = {
        name: "",
        coordinate: {}
      };
      this.drivers = [];
      this.driverName = "";
      this.selectedDriver = null;
      this.fare = 0;
      this.duration = 0;
      this.distance = 0;
      this.ticket = "";
      this.ticketId = "";
      this.ticketLoading = false;
      this.ticketError = false;
      this.date = new Date();
    });

    this.socket.on("searchingRent", () => {
      window.flash({
        type: "success",
        body: "The rent search has been dispatched!",
      });
      window.events.$emit("dispatched");
      this.searching = true;
      this.phoneNumber = "";
      this.riderName = "";
      this.email = "";
      this.riderId = "";
      // this.tripSearchId = data.tripSearchId
      this.destinationAddress = {
        name: "",
        coordinate: {}
      };
      this.pickUpAddress = {
        name: "",
        coordinate: {}
      };
      this.drivers = [];
      this.driverName = "";
      this.selectedDriver = null;
      this.fare = 0;
      this.duration = 0;
      this.distance = 0;
      this.ticket = "";
      this.ticketId = "";
      this.ticketLoading = false;
      this.ticketError = false;
      this.date = new Date();
    });


    this.getVehicleTypes();

    if (this.$route.query.tripSearch) {
      this.fetchTripSearch(this.$route.query.tripSearch).then(tripSearch => {
        const { passenger, vehicleType, pickUpAddress, dropOffAddress } = tripSearch.data;

        this.phoneNumber = passenger.phoneNumber.slice(4);
        this.riderName = passenger.firstName + " " + passenger.lastName;

        this.email = passenger.email || " - "

        this.vehicleType = vehicleType._id

        this.pickUpAddress = pickUpAddress
        if (!this.pickUpAddress.place_id) this.pickUpAddress = {
          name: this.pickUpAddress.name,
          coordinate: {
            lat: this.pickUpAddress.lat,
            long: this.pickUpAddress.long
          }
        }

        this.destinationAddress = dropOffAddress

        if (!this.destinationAddress.place_id) this.destinationAddress = {
          name: this.destinationAddress.name,
          coordinate: {
            lat: this.destinationAddress.lat,
            long: this.destinationAddress.long
          }
        }

        this.estimate()

        // alert(JSON.stringify(pickUpAddress, null, 2));
        // alert(JSON.stringify(dropOffAddress, null, 2));
      })
    }
  },
  computed: {
    // mapOptions() {
    //   return {
    //     center: {
    //       lat: 9.004912,
    //       lng: 38.778282,
    //     },
    //     zoom: 12,
    //   }
    // },
    // geoJsonSource() {
    //   return {
    //     type: 'geojson',
    //     data: {
    //       id: "thisIsMySource",
    //       type: "FeatureCollection",
    //       features: [
    //         {
    //           type: "Feature",
    //           geometry: {
    //             type: "LineString",
    //             coordinates: this.lineString
    //           },
    //           properties: {
    //             id: "value0"
    //           }
    //         }
    //       ]
    //     },
    //   }  
    // },
    baseFare() {
      if (this.type === "corporate") {
        return this.corporateEstimation.baseFare;
      } else {
        const selected = this.vehicleTypes.find(
          (v) => v._id === this.vehicleType
        );
        return selected && selected != undefined
          ? selected.baseFare
          : 0;
      }
    },
    estimatedFare() {
      if (this.type === "corporate") {
        if (this.distance)
          return (
            ((this.distance / 1000) * this.corporateEstimation.pricePerKM) +
            //this.duration * selected.pricePerMin +
            this.corporateEstimation.baseFare
          ).toFixed(2)
        return "-"
        // return this.corporateEstimation.estimatedFare ? this.corporateEstimation.estimatedFare.toFixed(2) : "";
      } else {
        const selected = this.vehicleTypes.find(
          (v) => v._id === this.vehicleType
        );

        if (this.type === "rent") {
          return (
            this.rentMonth * (selected.rentPerDay * 30) +
            this.rentDay * selected.rentPerDay +
            this.rentHour * selected.rentPerHour
          ).toFixed(2);
        } else {
          return selected && selected != undefined
            ? this.type == "bid"
              ? this.bidAmount
              : (
                ((this.distance / 1000) * selected.pricePerKM) +
                //this.duration * selected.pricePerMin +
                selected.baseFare
              ).toFixed(2)
            : 0;
        }
      }
    },
    validCorporate() {
      return this.selectedEmployee &&
        this.selectedCorporate &&
        this.pickUpAddress &&
        this.destinationAddress &&
        this.vehicleType
    },
    // estimatedArrivalTime() {
    //   if (this.selectedDriver) {
    //     return ((Number(this.distance) / 40) * 60).toFixed(2);
    //   } else return null;
    // },
    durationString() {
      const d = this.duration / 60
      var minute = d % 60;
      var hour = (d - minute) / 60;
      return hour + ":" + Math.round(minute).toString().padStart(2, "0");
    },
    id() {
      return this.$store.state.auth.account._id;
    },
  },
  watch: {
    ticket() {
      this.validateTicket();
    },
    phoneNumber() {
      this.validateTicket();
    },
    // customPickupAddress(customCoordinatesAreSet) {
    //   if (customCoordinatesAreSet === true && this.pickUpAddress.coordinate) {
    //     const geocoder = new this.google.maps.Geocoder();
    //     geocoder.geocode(
    //       {
    //         location: {
    //           lat: this.pickUpAddress.coordinate.lat,
    //           lng: this.pickUpAddress.coordinate.long,
    //         },
    //       },
    //       (results, status) => {
    //         if (status === "OK") {
    //           if (results[0]) {
    //             this.pickUpAddress.name = results[0].formatted_address;
    //             this.customPickupAddress = results[0].formatted_address;
    //           } else {
    //             window.alert("No results found");
    //           }
    //         } else {
    //           window.alert("Geocoder failed due to: " + status);
    //         }
    //       }
    //     );
    //   }
    // },
    pickUpAddress(newPickUpAddress) {
      //   this.map = new this.google.maps.Map(this.$refs.map, {
      //   center: {
      //     lat: 9.004912,
      //     lng: 38.778282,
      //   },
      //   zoom: 10,
      // });
      // if (newPickUpAddress && newPickUpAddress.place_id)
      //   this.customPickupAddress = false;

      // for (const marker in this.markers) {
      //   this.markers[marker].setMap(null);
      // }


      // var request = {
      //   placeId: newPickUpAddress.place_id,
      // };

      // const callback = (place, status) => {
      //   if (status == this.google.maps.places.PlacesServiceStatus.OK) {
      //     var marker = new this.google.maps.Marker({
      //       map: this.map,
      //       position: place.geometry.location
      //     });
      //     this.map.setCenter(marker.getPosition());
      //     this.map.setZoom(16)
      //   }
      // }

      // var marker = new this.google.maps.Marker({
      //   map: this.map,
      //   position: { lat: newPickUpAddress.coordinate.lat, lng: newPickUpAddress.coordinate.long },
      //   icon: "http://res.cloudinary.com/ika/image/upload/w_30,h_50/v1606686565/pxc38df2vfobmgmmxi95.png",
      // });
      this.mapCenter = [newPickUpAddress.coordinate.lat, newPickUpAddress.coordinate.long]

      this.mapZoom = 14

      // let service = new this.google.maps.places.PlacesService(this.map);
      // service.getDetails(request, callback);

      // this.markers.pickup = marker;

      console.log("+>@>", newPickUpAddress.coordinate.long);
      this.socket.emit("nearbyDrivers", {
        location: newPickUpAddress,
        vehicleType: this.vehicleType,
      });
    },
    destinationAddress(newDestinationAddress) {
      if (newDestinationAddress && newDestinationAddress.name) {
        // this.map.setCenter({
        //   lat: 9.004912,
        //   lng: 38.778282,
        // })

        // var marker = new this.google.maps.Marker({
        //   map: this.map,
        //   position: { lat: newDestinationAddress.coordinate.lat, lng: newDestinationAddress.coordinate.long },
        //   icon: "https://res.cloudinary.com/ika/image/upload/w_30,h_50/v1606686806/cot62kytpswv7lzdextc.png"
        // });
        this.mapCenter = {
          lat: 9.004912,
          lng: 38.778282,
        }
        this.mapZoom = 10
        this.markers = []
        // this.map.setCenter(marker.getPosition());
        // this.map.setZoom(10);

        // if (this.markers.dropOff) {
        //   this.markers.dropOff.setMap(null)
        // }

        // this.markers.dropOff = marker;
      }

    },
    vehicleType(newVal) {
      if (newVal) {
        // for (const marker in this.markers) {
        //   this.markers[marker].setMap(null);
        // }

        this.markers = []
        this.socket.emit("nearbyDrivers", {
          location: this.pickUpAddress,
          vehicleType: newVal,
        });
      }
    },
    nearbyDrivers(newData) {

      this.markers = newData.map(x => {
        return {
          id: x._id,
          coordinates: [x.position.coordinates[0], x.position.coordinates[1]],
          color: x.driver.isEmployee ? "blue" : "green"
        }
      })

      // newData.forEach((element) => {
      //   element.driver = element.driver ? element.driver : {};
      //   if (element.position) {
      //     var icon =
      //       "https://res.cloudinary.com/torpa/image/upload/e_tint:100:green/w_30,h_30/v1598171147/carMarker_vpibvl.png";
      //     if (this.markers[element._id]) {
      //       this.markers[element._id].setPosition({
      //         lat: element.position.coordinates[1],
      //         lng: element.position.coordinates[0],
      //       });
      //       this.markers[element._id].setIcon(icon);
      //     } else {
      //       this.markers[element._id] = new this.google.maps.Marker({
      //         position: {
      //           lat: element.position.coordinates[1],
      //           lng: element.position.coordinates[0],
      //         },
      //         icon,
      //         title: element.driver ? element.driver.fullName : "-",
      //       });

      //       this.markers[element._id].setMap(this.map);
      //       this.markers[element._id].addListener("click", () => {
      //         this.selectDriverFromMap(element._id);
      //       });
      //     }
      //   }
      // });
    },
    type(newValue) {
      if (newValue === 'corporate') {
        this.fetchCorporatesList();
      }
    },
    selectedCorporate(corporate) {
      const c = this.corporatesList.find(x => x._id === corporate)

      if (c && c.pricing) {
        this.corporateEstimation = c.pricing
      }

      this.fetchEmployeesList();
      this.estimate()
    }
  },
  methods: {
    // True once a search has an outcome, so the Dispatch button can stop
    // spinning. COMPLETED is the accepted case.
    searchIsResolved(status) {
      return ["COMPLETED", "CANCELLED", "NO_DRIVERS_FOUND"].includes(status);
    },
    setVehicleType(e) {
      if (e.target.value.trim() == "") {
        this.vehicleType = "";
        this.duration = 0;
        this.distance = 0;
        this.fare = 0;
        return;
      }

      this.duration = 0;
      this.distance = 0;
      this.fare = 0;
      this.vehicleType = e.target.value;
      this.estimate();
    },
    getVehicleTypes() {
      this.loading = true;
      this.$misc
        .get("vehicleTypes")
        .then((response) => {
          this.vehicleTypes = response.data.data;
          var vt = this.vehicleTypes.find((type) => type.name === "Any");
          if (vt) {
            this.vehicleType = vt._id;
          } else if (this.vehicleTypes.length) {
            this.vehicleType = this.vehicleTypes[0]._id;
          }
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loading = false));
    },
    fetchCorporatesList() {
      this.loadingCorps = true;
      this.$http
        .get("corporates/list-for-dispatcher")
        .then((response) => {
          this.corporatesList = response.data.data
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loadingCorps = false));
    },
    fetchEmployeesList() {
      this.loadingEmployees = true;
      this.$http
        .get("corporates/employees-list?corporate=" + this.selectedCorporate)
        .then((response) => {
          this.employeesList = response.data.data
        })
        .catch((err) => console.log(err))
        .finally(() => (this.loadingEmployees = false));
    },
    fetchTripSearch(tripSearchId) {
      this.fetchingTripSearch = true;
      var path = `trip-searches/${tripSearchId}`;

      return this.$http
        .get(path)
        .finally(() => (this.fetchingTripSearch = false));
    },
    changeFocus(current, next) {
      this.focused[current] = false;
      this.focused[next] = true;
    },
    onPaste(evt) {
      this.phoneNumber = evt.clipboardData.getData("text");
    },
    retry() {
      this.socket.emit("retry", { tripSearchId: this.tripSearch._id });
    },
    cancel() {
      this.socket.emit("cancel", { tripSearchId: this.tripSearch._id });
    },
    retryRent() {
      this.socket.emit("retryRent", { rentSearchId: this.rentSearch._id });
    },
    cancelRent() {
      this.socket.emit("cancelRent", { rentSearchId: this.rentSearch._id });
    },
    // Lists, in words, whatever is stopping a dispatch. Empty means good to go.
    missingDispatchFields() {
      const intPhone = parseInt(this.phoneNumber);
      const complete = (address) =>
        address &&
        address.name &&
        address.coordinate &&
        address.coordinate.lat &&
        address.coordinate.long;

      const missing = [];

      if (!intPhone || intPhone.toString().length != 9) {
        missing.push("enter a 9 digit phone number");
      }
      if (!complete(this.pickUpAddress)) {
        missing.push("choose a pickup address from the suggestions");
      }
      if (this.type != "rent" && !complete(this.destinationAddress)) {
        missing.push("choose a destination address from the suggestions");
      }
      if (!this.vehicleType) {
        missing.push("select a vehicle type");
      }
      if (this.type == "bid" && !(this.bidAmount >= this.baseFare)) {
        missing.push(`enter a bid of at least ${this.baseFare}`);
      }

      return missing;
    },
    add() {
      var intPhone = parseInt(this.phoneNumber);
      const missing = this.missingDispatchFields();

      // Silently doing nothing here is what makes the button look broken.
      if (missing.length) {
        window.flash({
          type: "error",
          body: `Cannot dispatch yet — please ${missing.join("; ")}.`,
        });
      }

      if (!missing.length) {
        var data = {
          pickUpAddress: this.pickUpAddress,
          dropOffAddress: this.destinationAddress,
          vehicleType: this.vehicleType,
          name: this.riderName,
          type: this.type,
          passengerId: this.riderId,
          email: this.email,
          phone: "+251" + intPhone.toString(),
          route: {
            distance: this.distance,
            duration: this.duration,
            polyline: this.polyline,
          }
        };

        if (this.scheduled && this.type == "normal") {
          var dateTime = new Date(this.date);
          dateTime.setHours(this.hour);
          dateTime.setMinutes(this.minute);
          data["schedule"] = dateTime;
        }
        if (this.selectedDriver && this.selectedDriver.vehicle) {
          data.vehicle = this.selectedDriver.vehicle._id;
          data.driver = this.selectedDriver._id;
        }

        if (this.type == "corporate" && this.ticketId) {
          data["ticket"] = this.ticketId;
        } else if (this.type == "corporate" && !this.ticketId) {
          this.ticketError = true;
          return;
        }

        if (this.type == "bid") {
          data["bidAmount"] = this.bidAmount;
        }

        if (this.type == "rent") {
          // var startTimestamp = new Date();
          // var endTimestamp = new Date();
          // endTimestamp.setHours(endTimestamp.getHours() + this.rentHour);
          // endTimestamp.setDate(endTimestamp.getDate() + this.rentDay);
          // endTimestamp.setMonth(endTimestamp.getMonth() + this.rentMonth);
          // data["startTimestamp"] = startTimestamp;
          // data["endTimestamp"] = endTimestamp;
          data["duration"] =
            this.rentHour + this.rentDay * 24 + this.rentMonth * 24 * 30;
          this.socket.emit("rent", data);
        } else {
          this.socket.emit("search", data);
        }

        this.dispatching = true;
      }
    },
    dispatchCorporate() {

      if (
        this.selectedEmployee &&
        this.selectedCorporate &&
        this.pickUpAddress &&
        (this.pickUpAddress.name && this.pickUpAddress.coordinate && this.pickUpAddress.coordinate.lat && this.pickUpAddress.coordinate.long) &&
        (this.destinationAddress && this.destinationAddress.name && this.destinationAddress.coordinate && this.destinationAddress.coordinate.lat && this.destinationAddress.coordinate.long) &&
        this.vehicleType
      ) {
        var data = {
          employee: this.selectedEmployee,
          corporate: this.selectedCorporate,
          pickUpAddress: this.pickUpAddress,
          dropOffAddress: this.destinationAddress,
          route: {
            distance: this.distance,
            duration: this.duration,
            polyline: this.polyline,
          },
          vehicleType: this.vehicleType,
          type: this.scheduled ? "corporateScheduled" : "corporate",
        };

        if (this.scheduled) {
          var dateTime = new Date(this.date);
          dateTime.setHours(this.hour);
          dateTime.setMinutes(this.minute);
          data["schedule"] = dateTime;
        }
        if (this.selectedDriver && this.selectedDriver.vehicle) {
          data.vehicle = this.selectedDriver.vehicle._id;
          data.driver = this.selectedDriver._id;
        }

        // if (this.type == "corporate" && this.ticketId) {
        //   data["ticket"] = this.ticketId;
        // } else if (this.type == "corporate" && !this.ticketId) {
        //   this.ticketError = true;
        //   return;
        // }

        this.socket.emit("searchCorporateFromDispatcher", data);

        this.dispatching = true;
      }

    },
    estimate() {
      if (this.vehicleType &&
        this.pickUpAddress.name && this.pickUpAddress.coordinate && this.pickUpAddress.coordinate.lat && this.pickUpAddress.coordinate.long &&
        this.destinationAddress.name && this.destinationAddress.coordinate && this.destinationAddress.coordinate.lat && this.destinationAddress.coordinate.long
      ) {
        var request = {
          origin: new this.google.maps.LatLng(this.pickUpAddress.coordinate.lat, this.pickUpAddress.coordinate.long),
          destination: new this.google.maps.LatLng(this.destinationAddress.coordinate.lat, this.destinationAddress.coordinate.long),
          travelMode: 'DRIVING'
        };

        this.directionService.route(request, (result, status) => {
          if (status != 'OK' || !result.routes || !result.routes.length) {
            window.flash({
              type: "error",
              body: "Could not estimate the route. Please try again.",
            });
            return;
          }

          this.polyline = result.routes[0].overview_polyline;
          this.distance = result.routes[0].legs[0].distance.value
          this.duration = result.routes[0].legs[0].duration.value

          // The map layer wants [long, lat] pairs; the polyline decodes to
          // [lat, long].
          this.route1 = polyline.decode(this.polyline).map(x => [x[1], x[0]])
        })
      }
    },
    validateTicket() {
      this.ticketError = false;
      this.ticketId = "";
      if (this.ticket.length == 10 && this.phoneNumber) {
        this.ticketLoading = true;
        this.$misc
          .get(`tickets/validate/${this.ticket}/+251${this.phoneNumber}`)
          .then((response) => {
            if (response && response.data) {
              this.ticketId = response.data;
              this.ticketLoading = false;
              this.ticketError = false;
            }
          })
          .catch((err) => {
            this.ticketError = true;
            console.log(err);
          })
          .finally(() => {
            this.ticketLoading = false;
          });
      }
    },
    searchDriver(name) {
      if (name) {
        this.$misc
          .get("drivers/search?vehicle=true&limit=5&q=" + name)
          .then((drivers) => {
            this.drivers = drivers.data.map((driver) => {
              if (driver.vehicle != null && driver.vehicle.online == true) {
                driver.disabled = false;
              } else {
                driver.disabled = true;
              }
              return driver;
            });
          })
          .catch((err) => console.log(err))
          .finally(() => (this.loading = false));
      } else {
        this.drivers = [];
      }
    },
    searchRiders(phone) {
      this.riderName = "";
      this.riderId = "";
      this.email = "";
      if (phone) {
        this.$misc
          .get("users/search?limit=5&q=" + phone)
          .then((passengers) => {
            this.passengers = passengers.data;
            var passenger = this.passengers.find(
              (passenger) => passenger.phoneNumber == "+251" + phone
            );
            if (passenger) {
              this.selectRider(passenger);
            }
          })
          .catch((err) => console.log(err))
          .finally(() => (this.loading = false));
      } else {
        this.passengers = [];
      }
    },
    selectDriver(driver) {
      if (!driver.disabled) {
        this.selectedDriver = driver;
        this.driverName =
          driver.firstName + " " + driver.lastName + " - " + driver.phoneNumber;
        this.drivers = [];
      }
    },
    selectRider(passenger) {
      if (passenger) {
        this.riderId = passenger._id;
        this.riderName = passenger.firstName + " " + passenger.lastName;
        this.email = passenger.email;
        var pn = passenger.phoneNumber.replace("+251", "");
        if (pn.length == 9 && pn[0] == "9") {
          this.phoneNumber = pn;
        } else if (pn.length == 10 && pn[0] == "0") {
          this.phoneNumber = pn.replace("0", "");
        }
        this.passengers = [];
      }
    },
    selectDriverFromMap(id) {
      const driverSelected = this.nearbyDrivers.find((x) => x._id === id);

      if (driverSelected) this.selectDriver(driverSelected.driver);
      this.selectedDriver.vehicle = { _id: driverSelected._id };

      this.estimatedArrivalTime = (
        4 +
        (Number(driverSelected.distance) / 15) * 60
      ).toFixed(2);
    },
  },
};
</script>
